import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';

import { createLogger } from '../utils/logger';

const logger = createLogger('AccessChecklist');
export interface AccessChecklistByPerson {
  person: {
    email: string;
    name: string;
    userId: string | null;
    role: 'successor' | 'keyholder';
    status: 'active' | 'pending';
  };
  grants: Array<{
    grantId: string;
    taskKey: string;
    taskDisplayName: string;
    entityId: string;
    grantedAt: string;
  }>;
}

export interface AccessChecklistByTask {
  task: {
    taskKey: string;
    taskDisplayName: string;
    entityId: string;
  };
  grants: Array<{
    grantId: string;
    grantedToEmail: string;
    grantedToName: string;
    grantedToUserId: string | null;
    role: 'successor' | 'keyholder';
    status: 'active' | 'pending';
    grantedAt: string;
  }>;
}

export interface RevokeGrantResponse {
  success: boolean;
  message: string;
  revokedGrant: {
    taskDisplayName: string;
    personName: string;
    personEmail: string;
  };
}

class AccessChecklistService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    return response;
  }

  /**
   * Get access checklist organized by person
   */
  async getChecklistByPerson(): Promise<AccessChecklistByPerson[]> {
    try {
      const response = await this.makeRequest('/api/unified-access/checklist/by-person', {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch checklist: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      logger.error('Error fetching checklist by person:', error);
      throw error;
    }
  }

  /**
   * Get access checklist organized by task
   */
  async getChecklistByTask(): Promise<AccessChecklistByTask[]> {
    try {
      const response = await this.makeRequest('/api/unified-access/checklist/by-task', {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch checklist: ${response.status}`);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      logger.error('Error fetching checklist by task:', error);
      throw error;
    }
  }

  /**
   * Revoke a specific grant
   */
  async revokeGrant(grantId: string): Promise<RevokeGrantResponse> {
    try {
      const response = await this.makeRequest('/api/unified-access/checklist/revoke', {
        method: 'POST',
        body: JSON.stringify({ grantId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to revoke access: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error revoking grant:', error);
      throw error;
    }
  }
}

export const accessChecklistService = new AccessChecklistService();
